/**
 * ObjectivePresetPicker
 *
 * Selector visual de los 18 presets de campaña (9 efemérides + 9 comportamentales).
 * El preset "custom" se renderiza por separado como bloque largo en
 * ObjectiveCreatePage, así no se mezcla con los presets con blueprint.
 */

import { Check } from "lucide-react";
import { PRESETS, TARGET_METRICS } from "../types";
import type { ObjectivePreset } from "../types";

interface ObjectivePresetPickerProps {
  selected: ObjectivePreset | null;
  onSelect: (preset: ObjectivePreset) => void;
}

const VISIBLE_PRESETS = PRESETS.filter((p) => p.preset !== "custom");

export function ObjectivePresetPicker({
  selected,
  onSelect,
}: ObjectivePresetPickerProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {VISIBLE_PRESETS.map((p, idx) => {
        const isSelected = selected === p.preset;
        return (
          <button
            key={p.preset}
            onClick={() => onSelect(p.preset)}
            className={`relative text-left rounded-2xl border-2 p-5 transition-all duration-300 overflow-hidden hover:-translate-y-0.5 hover:shadow-md ${
              isSelected
                ? "border-violet-400 shadow-lg shadow-violet-200/50"
                : "border-slate-200 hover:border-slate-300"
            }`}
            style={{
              background: p.bgGradient,
              animation: `slide-in 0.4s ease-out ${idx * 0.05}s backwards`,
            }}
          >
            {/* Selected indicator */}
            {isSelected && (
              <div className="absolute top-3 right-3 w-6 h-6 bg-violet-600 rounded-full flex items-center justify-center shadow-sm animate-fade-in">
                <Check size={14} className="text-white" />
              </div>
            )}

            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
              <div className="text-4xl leading-none">{p.emoji}</div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-slate-900 leading-tight">
                  {p.name}
                </h3>
                <p className="text-xs text-slate-600 mt-1 leading-snug">
                  {p.shortDescription}
                </p>
              </div>
            </div>

            {/* Body */}
            <p className="text-xs text-slate-600 leading-relaxed mb-3">
              {p.longDescription}
            </p>

            {/* Outcome */}
            <div className="bg-white/70 rounded-lg px-3 py-2 border border-white/80">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-0.5">
                Resultado esperado
              </p>
              <p className="text-xs text-slate-700 font-medium leading-tight">
                {p.exampleOutcome}
              </p>
            </div>

            {/* KPIs que la IA monitorea (chips) */}
            {p.trackedMetrics.length > 0 && (
              <div className="mt-2.5">
                <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                  KPIs que monitorea
                </p>
                <div className="flex flex-wrap gap-1">
                  {p.trackedMetrics.map((mKey) => {
                    const meta = TARGET_METRICS.find((t) => t.key === mKey);
                    if (!meta) return null;
                    return (
                      <span
                        key={mKey}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-white/80 text-slate-700 border border-white text-[10px] font-semibold"
                      >
                        <span>{meta.emoji}</span>
                        {meta.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default ObjectivePresetPicker;
